import { ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { ServerUrls } from '../../../../app/ServerUrls';
import { Messages } from '../../../../codegen/Localize';
import { openReactConfirmationDialog } from '../../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../../commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { rejectIfResultContainsError } from '../../../../httpClient/utils/HttpErrorChecker';
import WebHttpClient from '../../../../httpClient/WebHttpClient';
import { L10n } from '../../../../ts/L10n/L10n';
import { RunActiveDirectoryGroupsSyncRequest } from '../../../../ts/ServerApi/requests/RunActiveDirectoryGroupsSyncRequest';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { IServerTimestamps } from '../../../services/ServerSettings';
import { ServerSettingsUnauthenticated } from '../../../services/ServerSettingsUnauthenticated';
import { syncAllGroupsButtonContainer, timestampStyle, viewActivityLinkContainer } from '../../../styles/ActiveDirectorySyncSectionStyles';
import { learnMoreStyle } from '../../../styles/CommonStyles';
import { ServerSettingsProps } from '../ReactServerSettings';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export const AD_SYNC_SECTION_TEST_ID = 'ad-sync-server-setting';
export const AD_SYNC_LEARN_MORE_LINK_TEST_ID = 'scheduled-ad-sync-learn-more';
export const AD_SYNC_LAST_TIMESTAMP_TEST_ID = 'last-scheduled-ad-sync-time';
export const AD_SYNC_VIEW_ACTIVITY_LINK_TEST_ID = 'scheduled-ad-sync-activity';
export const AD_SYNC_ALL_GROUPS_BUTTON_TEST_ID = 'run-active-directory-groups-sync';

export type ActiveDirectorySyncSectionProps = ServerSettingsProps<'scheduledActiveDirectorySyncEnabled' | 'activeDirectorySyncScheduleFrequency'>;

export function ActiveDirectorySyncSection(props: ActiveDirectorySyncSectionProps): JSX.Element {
  if (!ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
    return null;
  }

  return (
    <SettingsSection
      title={Messages.activeDirectorySyncSchedule()}
      description={getDescription()}
      testId={AD_SYNC_SECTION_TEST_ID}
    >
      {renderLastSyncTime(props.serverTimestamps)}
      {renderViewActivityLink()}
      {renderSyncAllGroupsButton()}
      {
        // TODO (Part 2): render checkbox and schedule picker
      }
    </SettingsSection>
  );
}

function getDescription(): JSX.Element {
  return <>
    {Messages.activeDirectorySyncScheduleDetails()}
    <TextLinkWidget
      className={learnMoreStyle}
      href={HelpService.getServerHelpUrlByCategory('settings-adsync1')}
      target='_blank'
      testId={AD_SYNC_LEARN_MORE_LINK_TEST_ID}
    >
      {Messages.activeDirectorySyncScheduleLearnMoreLinkDescription()}
    </TextLinkWidget>
  </>;
}

function renderLastSyncTime(serverTimestamps: IServerTimestamps): JSX.Element {
  return <>
    {Messages.activeDirectorySyncScheduleLastSynchronized()}
    <span className={timestampStyle} data-tb-test-id={AD_SYNC_LAST_TIMESTAMP_TEST_ID}>
      {L10n.localizedDateTime(serverTimestamps.lastActiveDirectoryGroupsSyncTime)}
    </span>
    {Messages.activeDirectorySyncScheduleServerTimeLabel()}
  </>;
}

function renderViewActivityLink(): JSX.Element {
  return <div className={viewActivityLinkContainer}>
    <TextLinkWidget
      href={encodeURI(ServerUrls.getInstance().serverStatusVizUrl('BackgroundTasksforNonExtracts'))}
      target='_blank'
      testId={AD_SYNC_VIEW_ACTIVITY_LINK_TEST_ID}
    >
      {Messages.activeDirectorySyncScheduleViewUpdateLinkDescription()}
    </TextLinkWidget>
  </div>;
}

function renderSyncAllGroupsButton(): JSX.Element {
  return <div className={syncAllGroupsButtonContainer}>
    <ButtonWidget
      testId={AD_SYNC_ALL_GROUPS_BUTTON_TEST_ID}
      onClick={openConfirmationDialog}
    >
      {Messages.activeDirectorySyncScheduleRunNow_action()}
    </ButtonWidget>
  </div>;
}

function openConfirmationDialog(): void {
  const handleAPIsuccess = () => ToasterService.instance.pop({
    type: ToastType.Info,
    text: Messages.activeDirectorySyncScheduleRunNowSuccess()
  });
  const handleAPIerror = () => ToasterService.instance.pop({
    type: ToastType.Error,
    text: Messages.activeDirectorySyncScheduleRunNowError()
  });

  openReactConfirmationDialog({
    title: Messages.activeDirectorySyncScheduleConfirmTitle(),
    message: Messages.activeDirectorySyncScheduleConfirmBody(),
    actionName: Messages.activeDirectorySyncScheduleConfirmButton(),
    action: () => WebHttpClient.sendRequest(new RunActiveDirectoryGroupsSyncRequest())
      .then(rejectIfResultContainsError)
      .then(handleAPIsuccess, handleAPIerror),
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: null,
  });
}

/* for registering the component in settings registry */
export const ActiveDirectorySyncSectionSpecs = componentRegistryEntry({
  name: 'ActiveDirectorySyncSection',
  component: ActiveDirectorySyncSection
});
